import { useMemo } from "react";

import { compact } from "lodash";
import moment from "moment";
import {
  ArrayParam,
  BooleanParam,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { useUser } from "@components/user-context";
import { useDateRangeQueryState } from "@hooks/query-params";
import {
  type FetchOrderSummaryVariables,
  type SubscriptionTypeEnum,
  type ValueFloatComparisonInput,
  type ValueIntComparisonInput,
} from "@north-beam/nb-client-side";
import {
  CustomJsonParam,
  b64Decode,
  todayLocalAsISODate,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { usePrefs } from "@utils/client-side-preferences";
import { parseDateRangeArgument } from "@utils/index";

export enum OrderFilters {
  "subscriptions" = "subscriptions",
  "orderTags" = "orderTags",
  "sourceName" = "sourceName",
  "customerTags" = "customerTags",
  "orderTypes" = "orderTypes",
  "orderValue" = "orderValue",
  "discountValue" = "discountValue",
  "shippingValue" = "shippingValue",
  "taxValue" = "taxValue",
  "refundValue" = "refundValue",
  "products" = "products",
  "orderId" = "orderId",
  "orderNumber" = "orderNumber",
  "customerId" = "customerId",
  "numberOfOriginalItems" = "numberOfOriginalItems",
  "numberOfTouchpoints" = "numberOfTouchpoints",
  "attributed" = "attributed",
  "discountCodes" = "discountCodes",
}

export const ValueIntComparisonParam =
  CustomJsonParam<ValueIntComparisonInput | null>();
export const ValueFloatComparisonParam =
  CustomJsonParam<ValueFloatComparisonInput | null>();

export const SubscriptionArrayParam = CustomJsonParam<
  SubscriptionTypeEnum[] | null
>();

export const PaginationParam = CustomJsonParam<{
  pageIndex: number;
  pageSize: number;
} | null>();

export const useOrdersQueryParams = () =>
  useQueryParams(
    {
      [OrderFilters.subscriptions]: SubscriptionArrayParam,
      [OrderFilters.orderTags]: ArrayParam,
      [OrderFilters.sourceName]: ArrayParam,
      [OrderFilters.customerTags]: ArrayParam,
      [OrderFilters.orderTypes]: ArrayParam,
      [OrderFilters.orderValue]: ValueFloatComparisonParam,
      [OrderFilters.discountValue]: ValueFloatComparisonParam,
      [OrderFilters.shippingValue]: ValueFloatComparisonParam,
      [OrderFilters.taxValue]: ValueFloatComparisonParam,
      [OrderFilters.refundValue]: ValueFloatComparisonParam,
      [OrderFilters.products]: ArrayParam,
      [OrderFilters.orderId]: StringParam,
      [OrderFilters.orderNumber]: StringParam,
      [OrderFilters.customerId]: StringParam,
      [OrderFilters.numberOfOriginalItems]: ValueIntComparisonParam,
      [OrderFilters.numberOfTouchpoints]: ValueIntComparisonParam,
      [OrderFilters.attributed]: ArrayParam,
      [OrderFilters.discountCodes]: ArrayParam,
    },
    { updateType: "pushIn" },
  );

export const useOrdersPagination = () =>
  useQueryParam(
    "pagination",
    withDefault(PaginationParam, { pageIndex: 0, pageSize: 25 }),
    { updateType: "pushIn" },
  );

export const useSelectedConversionEventId = () =>
  useQueryParam("conversionEventId", StringParam, { updateType: "pushIn" });

export const useOrderPageAdjustForReturns = () =>
  useQueryParam("adjustForReturns", withDefault(BooleanParam, false), {
    updateType: "pushIn",
  });

export const useOrderPageEcommercePlatforms = () =>
  useQueryParam("ecommercePlatforms", ArrayParam, { updateType: "pushIn" });

export const useOrderPageAdPlatforms = () =>
  useQueryParam("adPlatforms", ArrayParam, { updateType: "pushIn" });

const stripPrefixAndDecode = <T extends string>(values: T[]): T[] => {
  return values?.map((t) => {
    const toDecodeIndx = t.indexOf("-");
    return b64Decode(t.slice(toDecodeIndx + 1)) as T;
  });
};

export const useOrdersPageFilters = () => {
  const [dateRangeState] = useDateRangeQueryState();
  const [adjustForReturns] = useOrderPageAdjustForReturns();
  const [adPlatforms] = useOrderPageAdPlatforms();
  const [ecommercePlatforms] = useOrderPageEcommercePlatforms();

  const prefs = usePrefs();
  const [
    {
      orderTags,
      sourceName,
      customerTags,
      subscriptions,
      orderTypes,
      orderValue,
      discountValue,
      shippingValue,
      taxValue,
      refundValue,
      products,
      customerId,
      numberOfOriginalItems,
      numberOfTouchpoints,
      attributed,
      discountCodes,
      orderId,
      orderNumber,
    },
  ] = useOrdersQueryParams();
  const { user } = useUser();

  // filter options
  const dateRange = parseDateRangeArgument(
    dateRangeState.dateRange,
    todayLocalAsISODate(),
  );

  const filterOptions: FetchOrderSummaryVariables["filterOptions"] =
    useMemo(() => {
      const isAnon = prefs.isDemoMode;
      const compactTags = compact(orderTags);
      const compactSourceName = compact(sourceName);
      const compactCustomerTags = compact(customerTags);
      const compactSubscriptions = compact(subscriptions);
      const compactDiscountCodes = compact(discountCodes);

      const decodedDiscountCodes = isAnon
        ? stripPrefixAndDecode(compactDiscountCodes)
        : compactDiscountCodes;

      const decodedTags = isAnon
        ? stripPrefixAndDecode(compactTags)
        : compactTags;
      const decodedSourceName = isAnon
        ? stripPrefixAndDecode(compactSourceName)
        : compactSourceName;
      const decodedCustomerTags = isAnon
        ? stripPrefixAndDecode(compactCustomerTags)
        : compactCustomerTags;

      const compactProducts = compact(products);
      const decodedProducts = isAnon
        ? stripPrefixAndDecode(compactProducts)
        : compactProducts;

      return {
        dateRange: {
          start: moment(dateRange.startDate)
            .tz(user.timezone)
            .startOf("day")
            .toDate(),
          end: moment(dateRange.endDate)
            .tz(user.timezone)
            .endOf("day")
            .toDate(),
        },
        discountCodes: decodedDiscountCodes?.length
          ? decodedDiscountCodes
          : null,
        subscriptions: compactSubscriptions?.length
          ? compactSubscriptions
          : null,
        customerTags: decodedCustomerTags?.length ? decodedCustomerTags : null,
        orderTags: decodedTags?.length ? decodedTags : null,
        sourceName: decodedSourceName?.length ? decodedSourceName : null,
        orderTypes: orderTypes?.length
          ? compact(
              orderTypes?.map((orderType) => {
                if (orderType === "First time") {
                  return "first_time";
                }
                if (orderType === "Returning") {
                  return "returning";
                }
                return null;
              }),
            )
          : null,
        orderValue,
        discountValue,
        shippingValue,
        taxValue,
        refundValue,
        products: decodedProducts?.length ? compact(decodedProducts) : null,
        orderId,
        customerId,
        orderNumber,
        numberOfOriginalItems: numberOfOriginalItems,
        numberOfTouchpoints: numberOfTouchpoints,
        attributed: attributed?.length ? compact(attributed) : null,
        adjustForReturns,
        adPlatforms: adPlatforms?.length ? compact(adPlatforms) : null,
        ecommercePlatforms: ecommercePlatforms?.length
          ? compact(ecommercePlatforms)
          : null,
      };
    }, [
      orderNumber,
      dateRange.startDate,
      dateRange.endDate,
      user.timezone,
      discountCodes,
      orderTags,
      sourceName,
      customerTags,
      subscriptions,
      orderTypes,
      orderValue,
      discountValue,
      shippingValue,
      taxValue,
      refundValue,
      products,
      orderId,
      customerId,
      numberOfOriginalItems,
      numberOfTouchpoints,
      attributed,
      adjustForReturns,
      adPlatforms,
      ecommercePlatforms,
      prefs.isDemoMode,
    ]);

  return filterOptions;
};

export enum APIGranularity {
  "DAY" = "DAY",
  "HOUR" = "HOUR",
  "MONTH" = "MONTH",
  "WEEK" = "WEEK",
}

const toAPIGranularityUnit = (granularity: ITimeGranularityV2) => {
  if (granularity === "daily") {
    return APIGranularity.DAY;
  } else if (granularity === "monthly") {
    return APIGranularity.MONTH;
  } else if (granularity === "weekly") {
    return APIGranularity.WEEK;
  } else {
    return APIGranularity.HOUR;
  }
};

export const useOrderGraphFilters = () => {
  const orderPageFilterOptions = useOrdersPageFilters();
  const [dateRangeState] = useDateRangeQueryState();

  const filterOptions = useMemo(
    () => ({
      ...orderPageFilterOptions,
      granularity: toAPIGranularityUnit(dateRangeState.timeGranularity),
    }),
    [dateRangeState.timeGranularity, orderPageFilterOptions],
  );

  return filterOptions;
};
